import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, CameraOff, ChevronUp, Heart, Info, MapPin, MessageCircle, MoreHorizontal, Play, Search, Send, UserPlus, Users } from 'lucide-react';
import { SNAPCHAT_APP_CTA_TYPES, SNAPCHAT_CTA_TYPES } from './snapchatConfig';

const PLACEMENTS = [
  { value: 'story', label: 'Stories (between friends’ stories)' },
  { value: 'spotlight', label: 'Spotlight' },
  { value: 'discover', label: 'Chat feed / Discover tile' },
];

const ctaLabel = (value, isApp) => (isApp ? SNAPCHAT_APP_CTA_TYPES : SNAPCHAT_CTA_TYPES).find((c) => c.value === value)?.label || 'More';

const Media = ({ formData, rounded = '' }) => {
  if (formData.videoPreview) return <video src={formData.videoPreview} className={`absolute inset-0 w-full h-full object-cover ${rounded}`} muted loop autoPlay playsInline />;
  if (formData.imagePreview) return <img src={formData.imagePreview} alt="" className={`absolute inset-0 w-full h-full object-cover ${rounded}`} />;
  return (
    <div className={`absolute inset-0 flex flex-col items-center justify-center text-white/60 bg-black ${rounded}`}>
      <CameraOff className="h-10 w-10" />
      <span className="text-[10px] mt-2">Upload 9:16 media to preview</span>
    </div>
  );
};

const SafeZone = ({ show }) => show && (
  <div className="absolute inset-0 pointer-events-none" data-testid="snapchat-preview-safe-zone">
    <div className="absolute inset-x-0 top-0 h-[14%] bg-red-500/25 border-b border-dashed border-red-300/70" />
    <div className="absolute inset-x-0 bottom-0 h-[20%] bg-red-500/25 border-t border-dashed border-red-300/70" />
    <div className="absolute inset-y-[14%] right-0 w-[16%] bg-red-500/15 border-l border-dashed border-red-300/50" />
    <span className="absolute top-[15%] left-2 text-[8px] text-red-100 bg-red-600/70 px-1 rounded">Keep text & logos inside the safe zone</span>
  </div>
);

const StatusBar = ({ dark = true }) => (
  <div className={`absolute top-0 inset-x-0 flex justify-between px-3 pt-2 text-[10px] font-semibold ${dark ? 'text-white' : 'text-black'}`}>
    <span>11:04</span><span>▂▄▆ ● ▮</span>
  </div>
);

const StoryFrame = ({ formData, cta, safe }) => (
  <div className="relative w-full h-full bg-black">
    <Media formData={formData} />
    <SafeZone show={safe} />
    <StatusBar />
    <div className="absolute top-6 inset-x-2 flex gap-1">{[0, 1, 2].map((i) => <div key={i} className={`h-0.5 flex-1 rounded ${i === 0 ? 'bg-white' : 'bg-white/40'}`} />)}</div>
    <div className="absolute top-8 left-3 flex items-center gap-2 text-white">
      <div className="w-7 h-7 rounded-full bg-[#FFFC00] text-black flex items-center justify-center text-[10px] font-bold">{(formData.snapBrandName || 'A')[0]}</div>
      <div className="leading-tight">
        <p className="text-[11px] font-semibold drop-shadow">{formData.snapBrandName || 'Brand name'}</p>
        <p className="text-[9px] text-white/80 drop-shadow">Ad · {formData.headline || 'Your headline'}</p>
      </div>
    </div>
    <div className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-1 text-white">
      <ChevronUp className="h-4 w-4 animate-bounce" />
      <div className="px-5 py-1.5 rounded-full bg-white text-black text-[11px] font-semibold shadow" data-testid="snapchat-preview-cta">{cta}</div>
    </div>
  </div>
);

const SpotlightFrame = ({ formData, cta, safe }) => (
  <div className="relative w-full h-full bg-black">
    <Media formData={formData} />
    <SafeZone show={safe} />
    <StatusBar />
    <div className="absolute top-6 inset-x-3 flex items-center justify-between text-white">
      <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-amber-300" /><Search className="h-4 w-4" /></div>
      <span className="text-[12px] font-bold">Spotlight</span>
      <UserPlus className="h-4 w-4" />
    </div>
    <div className="absolute right-3 bottom-[26%] flex flex-col items-center gap-3 text-white"><Heart className="h-5 w-5" /><Send className="h-5 w-5" /><MoreHorizontal className="h-5 w-5" /></div>
    <div className="absolute left-3 right-14 bottom-[24%] text-white">
      <span className="text-[9px] bg-white/25 px-1.5 py-0.5 rounded">Ad</span>
      <p className="text-[11px] font-semibold mt-1 drop-shadow">{formData.snapBrandName || 'Brand name'}</p>
      <p className="text-[10px] text-white/90 drop-shadow truncate">{formData.headline || 'Your headline'}</p>
    </div>
    <div className="absolute inset-x-3 bottom-[13%]"><div className="w-full py-1.5 rounded-full bg-white text-black text-center text-[11px] font-semibold" data-testid="snapchat-preview-cta">{cta}</div></div>
    <div className="absolute bottom-0 inset-x-0 h-[11%] bg-black flex items-center justify-around text-white/80 px-2"><MapPin className="h-4 w-4" /><MessageCircle className="h-4 w-4" /><Camera className="h-4 w-4" /><Users className="h-4 w-4" /><Play className="h-4 w-4 text-red-400" /></div>
  </div>
);

const DiscoverFrame = ({ formData, cta, safe }) => (
  <div className="relative w-full h-full bg-white text-black">
    <StatusBar dark={false} />
    <div className="absolute top-6 inset-x-3 flex items-center justify-between"><span className="text-[12px] font-bold">Stories</span><Search className="h-4 w-4" /></div>
    <div className="absolute top-12 inset-x-3 space-y-2">
      <p className="text-[10px] font-semibold text-black/60">Friends</p>
      <div className="flex gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="w-12 h-16 rounded-lg bg-gray-200" />)}</div>
      <p className="text-[10px] font-semibold text-black/60 pt-1">Discover</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] shadow" data-testid="snapchat-preview-tile">
          <Media formData={formData} rounded="rounded-xl" />
          <SafeZone show={safe} />
          <span className="absolute top-1.5 left-1.5 text-[8px] bg-white/80 text-black px-1 rounded">Ad</span>
          <div className="absolute bottom-0 inset-x-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent text-white">
            <p className="text-[9px] font-semibold truncate">{formData.snapBrandName || 'Brand name'}</p>
            <p className="text-[8px] truncate">{formData.headline || 'Your headline'}</p>
            <div className="mt-1 text-center text-[8px] bg-white text-black rounded-full py-0.5" data-testid="snapchat-preview-cta">{cta}</div>
          </div>
        </div>
        <div className="rounded-xl bg-gray-200 aspect-[9/16]" />
      </div>
    </div>
    <div className="absolute bottom-0 inset-x-0 h-[9%] bg-white border-t flex items-center justify-around text-black/70 px-2"><MapPin className="h-4 w-4" /><MessageCircle className="h-4 w-4" /><Camera className="h-4 w-4" /><Users className="h-4 w-4 text-black" /><Play className="h-4 w-4" /></div>
  </div>
);

export const SnapchatAdPreview = ({ formData }) => {
  const [placement, setPlacement] = useState('story');
  const [safe, setSafe] = useState(false);
  const isApp = formData.objective === 'APP_PROMOTION';
  const cta = ctaLabel(formData.snapCta || (isApp ? 'INSTALL_NOW' : 'MORE'), isApp);
  const Frame = placement === 'spotlight' ? SpotlightFrame : placement === 'discover' ? DiscoverFrame : StoryFrame;

  return (
    <div className="space-y-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="snapchat-ad-preview">
      <Label className="text-white/90 text-sm font-medium">Ad preview</Label>
      <div className="space-y-1.5">
        <Label className="text-white/60 text-xs">Placement option</Label>
        <Select value={placement} onValueChange={setPlacement}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white h-9" data-testid="snapchat-preview-placement-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/10">
            {PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-center">
        <div className="relative w-[230px] aspect-[9/19] rounded-[28px] border-[6px] border-slate-900 bg-black overflow-hidden shadow-2xl" data-testid={`snapchat-preview-frame-${placement}`}>
          <Frame formData={formData} cta={cta} safe={safe} />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <Switch checked={safe} onCheckedChange={setSafe} data-testid="snapchat-preview-safe-zone-switch" />
        <span className="text-xs text-white/70">Show content safe zone</span>
        <Info className="h-3 w-3 text-white/40" title="Snapchat UI may cover the top 14% and bottom 20% of your media" />
      </label>
    </div>
  );
};
